<script lang="ts" setup>
const props = defineProps({
  level: {
    default: 0,
    type: Number,
  },
  levelToOpen: {
    default: 0,
    type: Number,
  },
  objectKey: {
    default: undefined,
    type: [Number, String] as PropType<number | string>,
  },
  value: {
    default: undefined,
    type: [
      Array,
      Number,
      String,
      Object,
      Boolean,
    ] as PropType<
      | { [key: string]: any }
      | any[]
      | boolean
      | null
      | number
      | string
    >,
  },
})

const opened = ref(false)

const isArray = computed(() => {
  return Array.isArray(props.value)
})

const isArrayEmpty = computed(() => {
  return isArray.value && (props.value as []).length === 0
})

const isDelimiter = computed(() => {
  if (typeof props.value !== 'string') {
    return false
  }

  return [
    '[',
    ']',
    '{',
    '}',
  ].includes(props.value)
})

const isObject = computed(() => {
  return typeof props.value === 'object' && props.value !== null && !Array.isArray(props.value)
})

const isObjectEmpty = computed(() => {
  return isObject.value && Object.keys(props.value as { [key: string]: any }).length === 0
})

const isDeployable = computed(() => {
  return ((isArray.value && !isArrayEmpty.value) || (isObject.value && !isObjectEmpty.value))
})

const isLink = computed(() => {
  return typeof props.value === 'string' && (props.value.startsWith('http://') || props.value.startsWith('https://'))
})

// Watch
watch(
  () => props.levelToOpen,
  () => (opened.value = props.levelToOpen >= props.level),
  { immediate: true },
)

function onToggle() {
  if (isDeployable.value) {
    opened.value = !opened.value
  }
}
</script>

<template>
  <span class="relative block whitespace-nowrap text-sm text-gray-800 dark:text-gray-200">
    <UIcon
      v-if="isDeployable"
      class="transition-base absolute left-0 top-0 -ml-2 mt-1 size-4 transform cursor-pointer text-gray-500 hover:text-gray-900 dark:text-gray-500 dark:hover:text-gray-100"
      :class="{
        '-rotate-90': !opened,
        'rotate-0': opened,
      }"
      :name="getIcon('caret')"
      @click="onToggle"
    />

    <span
      class="flex items-start"
      :class="{
        'ml-0': level === 0,
        'ml-4': level === 1,
        'ml-8': level === 2,
        'ml-12': level === 3,
        'ml-16': level === 4,
        'ml-20': level === 5,
        'ml-24': level === 6,
        'ml-28': level === 7,
        'ml-32': level === 8,
        'ml-36': level === 9,
        'ml-40': level === 10,
      }"
    >
      <span
        v-if="objectKey && isNaN(parseInt(`${objectKey}`))"
        class="flex-initial text-indigo-700 dark:text-indigo-300"
        :class="{ 'cursor-pointer hover:text-gray-900 dark:hover:text-gray-100': isDeployable }"
        @click="onToggle"
      >
        {{ objectKey }}

        <span class="-ml-1 text-gray-900 dark:text-gray-100">:</span>
      </span>

      <span class="ml-1 flex-1">
        <span v-if="isDelimiter">{{ value }}</span>

        <span
          v-else-if="value === null"
          class="text-gray-600 dark:text-gray-400"
        >
          null
        </span>

        <span
          v-else-if="['boolean'].includes(typeof value)"
          class="text-purple-700 dark:text-purple-300"
        >
          {{ value }}
        </span>

        <span
          v-else-if="['bigint', 'number'].includes(typeof value)"
          class="text-orange-700 dark:text-orange-300"
        >
          {{ value }}
        </span>

        <component
          :is="isLink ? 'a' : 'span'"
          v-else-if="['string'].includes(typeof value)"
          class="text-teal-600 dark:text-teal-400"
          :class="{ 'cursor-pointer hover:text-gray-900 dark:hover:text-gray-100': isLink }"
          :href="isLink ? value : undefined"
          rel="nofollow noopener noreferrer"
          :target="isLink ? '_blank' : undefined"
        >
          "{{ value }}"
        </component>

        <span>{{ isDelimiter && level === 0 ? "" : isArrayEmpty ? "[ ]," : isArray ? "[" : isObjectEmpty ? "{ }," : isObject ? "{" : "," }}</span>

        <span
          v-if="isDeployable && !opened"
          class="group cursor-pointer"
          @click="onToggle"
        >
          <span class="text-gray-600 group-hover:text-gray-900 dark:text-gray-400 dark:group-hover:text-gray-100">…</span>

          <span class="text-gray-900 dark:text-gray-100">{{ isArray ? "]," : "}," }}</span>
        </span>
      </span>
    </span>

    <span
      v-if="isObject && !isObjectEmpty && opened"
      class="mt-1 grid grid-cols-1 gap-1.5"
    >
      <ApiResponseLine
        v-for="(childObjectValue, childObjectKey) in value"
        :key="childObjectKey"
        :level="level + 1"
        :level-to-open="levelToOpen"
        :object-key="childObjectKey"
        :value="childObjectValue"
      />

      <ApiResponseLine
        :level="level"
        value="}"
      />
    </span>

    <span
      v-if="isArray && !isArrayEmpty && opened"
      class="grid grid-cols-1 gap-1.5"
    >
      <ApiResponseLine
        v-for="(childObjectValue, childObjectKey) in value"
        :key="childObjectKey"
        :level="level + 1"
        :level-to-open="levelToOpen"
        :object-key="childObjectKey"
        :value="childObjectValue"
      />

      <ApiResponseLine
        :level="level"
        value="]"
      />
    </span>
  </span>
</template>
